import { inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import {
  BD_RELOAD_PAGE,
  BdUserTrackingService,
  reloadIfAllowed,
} from '@bindoc-projects/user-lib/data';

interface VersionInfo {
  hash: string;
}

const VERSION_CHECK_INTERVAL_MS = 60_000;
const VERSION_ENDPOINT = '/version.json';

@Injectable({ providedIn: 'root' })
export class BdVersionCheckService {
  private readonly _router = inject(Router);
  private readonly _ngZone = inject(NgZone);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _reload = inject(BD_RELOAD_PAGE);
  private readonly _tracking = inject(BdUserTrackingService);

  private _currentHash: string | null = null;
  private _newHash: string | null = null;
  private _newVersionAvailable = false;
  private _intervalId: ReturnType<typeof setInterval> | null = null;

  init(): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    this._fetchVersion().then((info) => {
      if (info) {
        this._currentHash = info.hash;
      }
    });

    this._startPolling();
    this._listenForNavigation();
  }

  private _startPolling(): void {
    this._ngZone.runOutsideAngular(() => {
      this._intervalId = setInterval(() => {
        this._checkForUpdate();
      }, VERSION_CHECK_INTERVAL_MS);
    });
  }

  private _stopPolling(): void {
    if (this._intervalId !== null) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
  }

  private async _checkForUpdate(): Promise<void> {
    const info = await this._fetchVersion();
    if (info && this._currentHash && info.hash !== this._currentHash) {
      this._newHash = info.hash;
      this._newVersionAvailable = true;
      this._stopPolling();
      // Deploy-adoption signal: a client has observed a newer build than the
      // one it booted with. The reload itself fires on the next navigation.
      this._tracking.trackEvent('app_update.detected', {
        currentHash: this._currentHash,
        newHash: info.hash,
      });
    }
  }

  private _listenForNavigation(): void {
    this._router.events
      .pipe(
        filter(
          (event): event is NavigationStart => event instanceof NavigationStart,
        ),
      )
      .subscribe((event) => {
        if (this._newVersionAvailable) {
          // Hard-navigate to where the user is heading (not the current page)
          // so their click isn't lost and they land there running fresh code.
          const outcome = reloadIfAllowed(this._reload, event.url);
          if (outcome === 'reloaded') {
            this._tracking.trackEvent('app_update.reload', {
              reason: 'version',
              targetUrl: event.url,
              fromHash: this._currentHash,
              toHash: this._newHash,
            });
          } else if (outcome === 'suppressed') {
            this._tracking.trackEvent('app_update.reload_suppressed', {
              reason: 'version',
            });
          }
        }
      });
  }

  private async _fetchVersion(): Promise<VersionInfo | null> {
    try {
      const response = await fetch(VERSION_ENDPOINT, { cache: 'no-store' });
      if (!response.ok) {
        return null;
      }
      return (await response.json()) as VersionInfo;
    } catch {
      return null;
    }
  }
}
